/* eslint-disable security/detect-object-injection */
/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import * as React from 'react';
import { IWebgroupFiltersData } from './webgroup-filters.data';
import { IWebgroupFiltersProps, IWebgroupFiltersResources } from './webgroup-filters.props.autogenerated';
import { WebGroupFilter } from './components/webgroup-filter';
import { Drawer, Module, IModuleProps } from '@msdyn365-commerce-modules/utilities';
import _remove from 'lodash/remove';
import { ProductRefiner, ProductRefinerValue } from '@msdyn365-commerce/retail-proxy';
import { FiltersSortOrder, SortOrder } from '../../actions/get-filters-sorting-data.action';
//import { filter } from 'lodash';

export interface IWebgroupFiltersState {
    isDrawerOpen: boolean;
    drawerListId: number | undefined;
    openDrawerList: number[];
    filters: ProductRefiner[];
    accordions: JSX.Element[];
    loaded: boolean;
    selectedFilters: IWebgroupFilter[];
    sortOrder: FiltersSortOrder[] | [];
    resources: IWebgroupFiltersResources;
}

export interface IWebgroupFilter {
    FilterName: string;
    FilterValues: string[];
}
export interface IWebGroupFilterGroup {
    name: string;
    filters: ProductRefiner[];
    filterChange: any;
}

export interface IWebgroupFiltersExtendedProps extends IWebgroupFiltersProps<IWebgroupFiltersData> {
    accordions: (JSX.Element | null)[];
    clearFilters: () => void;
}

/**
 *
 * WebgroupFilters component
 * @extends {React.PureComponent<IWebgroupFiltersProps<IWebgroupFiltersData>>}
 */
class WebgroupFilters extends React.PureComponent<IWebgroupFiltersExtendedProps, IWebgroupFiltersState> {
    public constructor(props: IWebgroupFiltersExtendedProps) {
        super(props);
        this.state = {
            openDrawerList: [],
            drawerListId: undefined,
            isDrawerOpen: false,
            selectedFilters: [],
            accordions: [],
            filters: [],
            loaded: false,
            sortOrder: [],
            resources: {} as IWebgroupFiltersResources
        };
    }
    private _renderCellData(group: any): JSX.Element | null {
        return <WebGroupFilter name={group.KeyName} filters={group.Values} filterChange={this._changeFitler} key={group.RecordId} />;
    }
    private readonly _toggle = (drawerListId: number | undefined) => (): void => {
        if (drawerListId) {
            if (this.state.openDrawerList.length) {
                let drawerList = this.state.openDrawerList.slice(0);
                if (this.state.openDrawerList.indexOf(drawerListId) > -1) {
                    drawerList = drawerList.filter(v => v !== drawerListId);
                } else {
                    drawerList.push(drawerListId);
                }
                this.setState({ openDrawerList: drawerList });
            } else {
                this.setState({ openDrawerList: [drawerListId] });
            }
        }

        if (this.state.drawerListId !== drawerListId) {
            this.setState({ drawerListId, isDrawerOpen: true });
        } else {
            this.setState({ drawerListId: undefined, isDrawerOpen: false });
        }
    };
    private _createDrawerBody(): void {
        const tempOpenDrawerList: number[] = [];
        let accordion: any = [];
        this._sortFilters();
        if (this.state.filters?.length) {
            accordion = this.state.filters.map((group: any, index: number) => {
                if (group.Values.length) {
                    const cellData: JSX.Element | null = this._renderCellData(group);
                    tempOpenDrawerList.push(group.RecordId!);
                    const accordionItemContainer: IModuleProps = {
                        moduleProps: this.props,
                        className: 'ms-filter-title'
                    };
                    return (
                        <Module {...accordionItemContainer} key={group.RecordId}>
                            <Drawer
                                key={index}
                                collapseProps={{
                                    timeout: 0,
                                    isOpen: this.state.openDrawerList.indexOf(group.RecordId ?? 0) > -1 ? true : false
                                }}
                                className='ms-filter__drawer'
                                openGlyph='ms-filter__drawer-open'
                                closeGlyph='ms-filter__drawer-close'
                                glyphPlacement='end'
                                toggleButtonText={group.KeyName}
                                onToggle={this._toggle(group.RecordId)}
                            >
                                <div className='ms-filter-details'>
                                    <span className='ms-filter-details'>{cellData}</span>
                                </div>
                            </Drawer>
                        </Module>
                    );
                }
                return null;
            });
        }
        this.setState({ accordions: accordion, loaded: true });
    }
    private readonly _changeFitler = (event: any, filter: ProductRefinerValue, name: string): void => {
        const value = filter.LeftValueBoundString || '';
        const fgroupg = this.state.selectedFilters.find(f => f.FilterName === name);

        if (event.currentTarget.checked) {
            if (fgroupg) {
                fgroupg.FilterValues.push(value);
            } else {
                this.state.selectedFilters.push({
                    FilterName: name,
                    FilterValues: [value]
                });
            }
        } else if (this.state.selectedFilters) {
            if (fgroupg?.FilterValues.length === 1) {
                _remove(this.state.selectedFilters as any, (f: IWebgroupFilter) => f.FilterName === name);
            } else {
                const index = fgroupg?.FilterValues.indexOf(value);
                // @ts-ignore: Object is possibly 'null'.
                if (index !== -1) {
                    // @ts-ignore: Object is possibly 'null'.
                    fgroupg?.FilterValues.splice(index, 1);
                }
            }
        }

        document.dispatchEvent(new CustomEvent('FILTERS_UPDATED', { detail: this.state.selectedFilters }));
    };

    private readonly _clearFilters = (): void => {
        this.setState({ selectedFilters: [] });
        document.dispatchEvent(new CustomEvent('FILTERS_UPDATED', { detail: [] }));
        const filterInputs = document.getElementsByClassName('filter-checkbox');

        for (let i = 0; i < filterInputs.length; i++) {
            (filterInputs[i] as HTMLInputElement).checked = false;
        }
    };

    private readonly quickSort = (arr: SortOrder[]): SortOrder[] => {
        if (arr.length <= 1) {
            return arr;
        }

        const pivot = arr[arr.length - 1];
        const leftArr = [];
        const rightArr = [];

        for (let i = 0; i < arr.length - 1; i++) {
            if (this.compareSortOrders(arr[i], pivot)) {
                rightArr.push(arr[i]);
            } else {
                leftArr.push(arr[i]);
            }
        }

        return [...this.quickSort(leftArr), pivot, ...this.quickSort(rightArr)];
    };

    // private readonly shuffle = (array: SortOrder[]) => {
    //     for (let i = array.length - 1; i > 0; i--) {
    //         const j = Math.floor(Math.random() * (i + 1));
    //         [array[i], array[j]] = [array[j], array[i]];
    //     }
    //     return array;
    // };

    private readonly compareSortOrders = (a: SortOrder, b: SortOrder): boolean => {
        const numA: number = parseFloat(a.SortOrder);
        const numB: number = parseFloat(b.SortOrder);
        if (numA === numB) {
            const charA = a.SortOrder.charAt(a.SortOrder.length - 1);
            const charB = b.SortOrder.charAt(b.SortOrder.length - 1);

            return charA > charB;
        } else {
            return numA > numB;
        }
    };

    private readonly _sortFilters = (): void => {
        const filters = this.state.filters.sort((a: any, b: any) => a.KeyName.localeCompare(b.KeyName));
        filters.forEach(f => {
            let name = f.KeyName || '';
            if (name !== this.state.resources.diamImperial && name !== this.state.resources.diamMetric) {
                name = this.state.resources.sizeLengthDiam;
            }

            // @ts-ignore
            const filterSortList = this.state.sortOrder[`${name}`] as SortOrder[];

            //filterSortList.sort((a: SortOrder, b: SortOrder) => this.compareSortOrders(a, b));
            const newFilterSortList = this.quickSort(filterSortList);
            if (f.Values && newFilterSortList) {
                const len = newFilterSortList.length > f.Values.length ? newFilterSortList.length : f.Values.length;
                f.Values.forEach((fv, index) => (fv.RowNumber = len + index));
                for (let i = 0; i < newFilterSortList.length; i++) {
                    for (let j = 0; j < f.Values.length; j++) {
                        if (newFilterSortList[i].Value === f.Values[j].LeftValueBoundString) {
                            f.Values[j].RowNumber = i;
                            break;
                        }
                    }
                }
                f.Values = f.Values.sort((a: any, b: any) => (a.RowNumber > b.RowNumber ? 1 : -1));
            }
        });
        this.setState({ filters: filters });
    };

    public render(): JSX.Element | null {
        const {
            data: { filters, filtersSortOrder },
            resources
        } = this.props;
        if (!this.state.loaded && filters?.result) {
            this.setState(
                {
                    filters: filters.result,
                    sortOrder: filtersSortOrder?.result?.FiltersSortOrder || [],
                    resources: resources
                },
                this._createDrawerBody
            );
        }
        const viewProps = {
            ...this.props,
            accordions: this.state.accordions,
            clearFilters: this._clearFilters
        };
        return this.props.renderView(viewProps);
    }
}

export default WebgroupFilters;
